import React, { useState, useRef, useCallback, useEffect } from "react";
import { ArrowUp, X, Type } from "lucide-react";
import type { SelectionInfo } from "../types";

interface SelectionActionBarProps {
  selection: SelectionInfo;
  onSubmit: (instruction: string, selectedText: string) => void;
  onDismiss: () => void;
  disabled?: boolean;
}

export function SelectionActionBar({
  selection,
  onSubmit,
  onDismiss,
  disabled = false,
}: SelectionActionBarProps) {
  const [instruction, setInstruction] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // No auto-focus — user may want to keep working in the document

  const handleSubmit = useCallback(() => {
    const trimmed = instruction.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed, selection.text);
    setInstruction("");
  }, [instruction, disabled, onSubmit, selection.text]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      }
      if (e.key === "Escape") {
        onDismiss();
      }
    },
    [handleSubmit, onDismiss]
  );

  const preview =
    selection.text.length > 80
      ? `${selection.text.substring(0, 80)}...`
      : selection.text;

  return (
    <>
      {/* Backdrop that dims the chat area */}
      <div
        className="absolute inset-0 z-10 bg-surface/70 backdrop-blur-[2px] pointer-events-auto"
        onClick={onDismiss}
        style={{ top: 0 }}
      />

      {/* Centered floating action bar */}
      <div className="absolute inset-x-0 z-20 mx-3 top-1/2 -translate-y-1/2 cai-fade-up">
        <div className="cai-gradient-border rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.2)]">
          <div className="bg-surface-glass backdrop-blur-xl">
            {/* Selection preview */}
            <div className="px-3.5 pt-3 pb-1.5 flex items-start gap-2.5">
              <div className="w-5 h-5 rounded-md bg-accent-violet/15 flex items-center justify-center shrink-0 mt-0.5">
                <Type size={11} className="text-accent-violet" />
              </div>
              <p className="text-[11px] text-text-secondary leading-snug flex-1 line-clamp-2">
                &ldquo;{preview}&rdquo;
                {selection.truncated && (
                  <span className="text-text-muted italic"> + more</span>
                )}
              </p>
              <button
                onClick={onDismiss}
                className="w-5 h-5 rounded-md flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-surface-raised transition-colors shrink-0"
                aria-label="Dismiss selection"
              >
                <X size={12} />
              </button>
            </div>

            {/* Instruction input */}
            <div className="px-3.5 pb-3 flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="What should I do with this?"
                disabled={disabled}
                className="flex-1 bg-surface-raised border border-border-subtle rounded-xl px-3 py-2 text-[12px] text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-violet/50 focus:shadow-[0_0_0_3px_rgba(167,139,250,0.1)] transition-all"
              />
              <button
                onClick={handleSubmit}
                disabled={!instruction.trim() || disabled}
                aria-label="Apply to selection"
                className="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0 cai-glow-btn"
              >
                <ArrowUp size={13} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
